import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Check, GraduationCap, Video, Clock } from 'lucide-react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Label } from './ui/label';

const AUDIOCURSOS_PACKAGES = [
  {
    id: null,
    name: 'Sin AudioCurso',
    price: 0,
    description: 'No incluir servicio de audiocurso',
    features: []
  },
  {
    id: 'ac_essential',
    name: 'Essential',
    price: 997,
    description: '1hr de Grabación en tiempo real. Post-producción de Audio. Incluye hasta 2 micrófonos.',
    features: [
      '1 hora de grabación en tiempo real',
      'Post-producción de Audio',
      'Hasta 2 micrófonos',
      '12 Shorts de 10s',
      'Iluminación y Sets Locales'
    ]
  },
  {
    id: 'ac_platino',
    name: 'Platino',
    price: 3500,
    description: '1hr de Grabación de Video a 1 Cámara. Post-producción de Audio y Video.',
    features: [
      '1 hora de grabación en tiempo real',
      'Video a 1 Cámara',
      'Post-producción de Audio y Video',
      '12 Shorts de 10s',
      'Iluminación y Sets Locales'
    ]
  },
  {
    id: 'ac_premium',
    name: 'Premium',
    price: 5500,
    description: '1hr de Grabación de Video a 2 Cámaras. Post-producción completa de Audio y Video.',
    features: [
      '1 hora de grabación en tiempo real',
      'Video a 2 Cámaras',
      'Post-producción de Audio y Video',
      '12 Shorts de 10s',
      'Iluminación y Sets Locales'
    ]
  }
];

const AUDIOCURSOS_ADDONS = [
  {
    id: 'ac_set_basico',
    name: 'Construcción de Set Básico',
    price: 5500,
    perProgram: false
  },
  {
    id: 'ac_set_virtual',
    name: 'Diseño de Set Virtual',
    price: 2500,
    perProgram: false
  }
];

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-700">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-semibold text-lg">{title}</span>
          {badge && (
            <span className="px-2 py-1 bg-[#aa2aff]/20 text-[#aa2aff] text-sm rounded-full font-medium">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-[#080c2d]/50 border-t border-white/10">
          {children}
        </div>
      )}
    </div>
  );
}

function PackageCard({ pkg, isSelected, onSelect, formatPrice, currency }) {
  return (
    <button
      onClick={() => onSelect(pkg.id)}
      className={cn(
        "flex flex-col p-4 rounded-xl border text-left transition-all",
        isSelected
          ? "border-[#aa2aff] bg-[#aa2aff]/10 shadow-[0_0_15px_rgba(170,42,255,0.2)]"
          : "border-white/10 hover:border-white/20 bg-white/5"
      )}
    >
      <div className="flex justify-between items-start mb-2">
        <h4 className={cn(
          "font-semibold",
          isSelected ? "text-[#aa2aff]" : "text-white"
        )}>
          {pkg.name}
        </h4>
        <span className="text-white font-bold">
          {pkg.price > 0 ? `${formatPrice(pkg.price)} ${currency}/hr` : 'Gratis'}
        </span>
      </div>
      <p className="text-slate-400 text-sm mb-3">{pkg.description}</p>
      {pkg.features.length > 0 && (
        <ul className="space-y-1">
          {pkg.features.slice(0, 3).map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-xs text-slate-300">
              <Check className="w-3 h-3 text-[#aa2aff]" />
              {feature}
            </li>
          ))}
        </ul>
      )}
    </button>
  );
}

export function AudioCursosSection({
  isOpen,
  onToggle,
  selectedPackage,
  onPackageSelect,
  selectedAddons = [],
  onAddonToggle,
  audiocursosQuantity = 1,
  onQuantityChange,
  audiocursosData = {},
  onAudiocursosDataChange,
  formatPrice,
  currency = 'MXN',
  servicePrices = {}
}) {
  const audiocursosPackages = [
    { ...AUDIOCURSOS_PACKAGES[0] },
    {
      ...AUDIOCURSOS_PACKAGES[1],
      price: servicePrices.price_ac_essential || AUDIOCURSOS_PACKAGES[1].price
    },
    {
      ...AUDIOCURSOS_PACKAGES[2],
      price: servicePrices.price_ac_platino || AUDIOCURSOS_PACKAGES[2].price
    },
    {
      ...AUDIOCURSOS_PACKAGES[3],
      price: servicePrices.price_ac_premium || AUDIOCURSOS_PACKAGES[3].price
    }
  ];

  const audiocursosAddons = [
    {
      id: 'ac_set_basico',
      name: 'Construcción de Set Básico',
      price: servicePrices.price_ac_set_basico || 5500,
      perProgram: false
    },
    {
      id: 'ac_set_virtual',
      name: 'Diseño de Set Virtual',
      price: servicePrices.price_ac_set_virtual || 2500,
      perProgram: false
    }
  ];

  const selectedPkg = audiocursosPackages.find(p => p.id === selectedPackage);
  
  // Calculate addons
  const addonsOneTime = selectedAddons.reduce((sum, addonId) => {
    const addon = audiocursosAddons.find(a => a.id === addonId);
    return sum + (addon && !addon.perProgram ? addon.price : 0);
  }, 0);
  
  const addonsPerProgram = selectedAddons.reduce((sum, addonId) => {
    const addon = audiocursosAddons.find(a => a.id === addonId);
    return sum + (addon && addon.perProgram ? addon.price : 0);
  }, 0);

  // Calculate estimated recording hours with factor 2.5 (same as podcasts)
  const AUDIOCURSOS_FACTOR = 2.5;
  const estimatedRecordingHours = audiocursosData.episodeDuration 
    ? (audiocursosQuantity * audiocursosData.episodeDuration * AUDIOCURSOS_FACTOR) / 60 
    : 0;

  // Total calculation
  const packageTotal = selectedPkg?.price 
    ? (estimatedRecordingHours > 0 
        ? (selectedPkg.price * estimatedRecordingHours) + (addonsPerProgram * audiocursosQuantity) + addonsOneTime
        : (selectedPkg.price * audiocursosQuantity) + (addonsPerProgram * audiocursosQuantity) + addonsOneTime)
    : 0;
    
  const badge = selectedPackage ? `${formatPrice(packageTotal)}` : null;

  const handleQuantityChange = (value) => {
    const numValue = parseInt(value, 10);
    if (numValue >= 1 && numValue <= 500) {
      onQuantityChange(numValue);
    }
  };

  const handleDataChange = (field, value) => {
    onAudiocursosDataChange({ ...audiocursosData, [field]: value });
  };

  return (
    <CollapsibleSection
      title="Cursos (A/V)"
      icon={GraduationCap}
      isOpen={isOpen}
      onToggle={onToggle}
      badge={badge}
    >
      <div className="space-y-4">
        {/* AudioCursos Data Section */}
        <div className="bg-[#1a1f50]/50 rounded-xl p-4 border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-[#aa2aff]" />
            Datos del AudioCurso
          </h3>
          
          <div className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Título del AudioCurso</Label>
              <Input
                placeholder="Ej: Curso de Marketing Digital"
                value={audiocursosData.title || ''}
                onChange={(e) => handleDataChange('title', e.target.value)}
                className="bg-[#080c2d] border-[#272b96] text-white"
              />
            </div>

            {/* Quantity */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Cantidad de Lecciones</Label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={audiocursosQuantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  className="bg-[#080c2d] border-[#272b96] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Duración por Lección (min)</Label>
                <Input
                  type="number"
                  min="1"
                  max="180"
                  placeholder="Ej: 30"
                  value={audiocursosData.episodeDuration || ''}
                  onChange={(e) => handleDataChange('episodeDuration', parseInt(e.target.value) || 0)}
                  className="bg-[#080c2d] border-[#272b96] text-white"
                />
              </div>
            </div>

            {/* Recording Hours Calculation */}
            {audiocursosData.episodeDuration > 0 && (
              <div className="bg-[#080c2d] rounded-lg p-3 border border-[#272b96]/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#aa2aff]" />
                    <span className="text-slate-300 text-sm">Horas de Grabación Estimadas:</span>
                  </div>
                  <span className="text-white font-bold">{estimatedRecordingHours.toFixed(1)} hrs</span>
                </div>
                <p className="text-slate-500 text-xs mt-1">
                  Fórmula: ({audiocursosQuantity} lecciones × {audiocursosData.episodeDuration} min × 2.5) ÷ 60
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Package Selection */}
        <div>
          <h3 className="text-white font-semibold mb-3">Selecciona tu Paquete</h3>
          <p className="text-slate-400 text-sm mb-4">
            {estimatedRecordingHours > 0 
              ? `Precio por hora de grabación × ${estimatedRecordingHours.toFixed(1)} horas estimadas`
              : `Precio por lección × ${audiocursosQuantity} lecciones`}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {audiocursosPackages.map(pkg => (
              <PackageCard
                key={pkg.id || 'none'}
                pkg={pkg}
                isSelected={selectedPackage === pkg.id}
                onSelect={onPackageSelect}
                formatPrice={formatPrice}
                currency={currency}
              />
            ))}
          </div>
        </div>

        {/* Addons */}
        {selectedPackage && (
          <div>
            <h3 className="text-white font-semibold mb-3">Extras Opcionales</h3>
            <div className="space-y-2">
              {audiocursosAddons.map(addon => (
                <label
                  key={addon.id}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all",
                    selectedAddons.includes(addon.id)
                      ? "border-[#aa2aff] bg-[#aa2aff]/10"
                      : "border-white/10 hover:border-white/20 bg-white/5"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded border-2 flex items-center justify-center",
                      selectedAddons.includes(addon.id)
                        ? "border-[#aa2aff] bg-[#aa2aff]"
                        : "border-slate-500"
                    )}>
                      {selectedAddons.includes(addon.id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-white text-sm">{addon.name}</span>
                  </div>
                  <span className="text-white font-bold text-sm">
                    +{formatPrice(addon.price)} {currency}
                  </span>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={selectedAddons.includes(addon.id)}
                    onChange={() => onAddonToggle(addon.id)}
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Total Summary */}
        {selectedPackage && packageTotal > 0 && (
          <div className="pt-4 border-t border-white/10">
            <div className="flex justify-between items-center text-lg">
              <span className="text-white font-semibold">Subtotal Cursos (A/V):</span>
              <span className="text-[#aa2aff] font-bold">{formatPrice(packageTotal)} {currency}</span>
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export default AudioCursosSection;
